import React from "react";
import { FaFileAlt, FaBarcode, FaTrash } from "react-icons/fa";
import api from "../../../services/api";

const GS = String.fromCharCode(29);

const allowedStatuses = ["ожидает", "в обработке", "обработан", "отменен"];

const statusColors = {
  ожидает: "#f7c948",
  "в обработке": "#2d9cdb",
  обработан: "#27ae60",
  отменен: "#eb5757",
};

const normalizeMark = (mark) => {
  if (!mark) return "";
  return mark[0] === GS ? mark.slice(1) : mark;
};

const DocumentCard = ({ document: doc, onStatusChange, onDelete }) => {
  const [copied, setCopied] = React.useState(false);
  const [status, setStatus] = React.useState(doc.document_status);
  const [showConfirm, setShowConfirm] = React.useState(false); // 🔥 модалка удаления

  const formatDate = (d) => (d ? new Date(d).toLocaleDateString() : "—");
  const formatDateTime = (d) => (d ? new Date(d).toLocaleString() : "—");

  // 🔥 Удаление документа
  const deleteDocument = async () => {
    try {
      await api.delete(`/externaltokens/delete-document/${doc.document_id}`);
      setShowConfirm(false);

      if (onDelete) onDelete(doc.document_id);
    } catch (err) {
      console.error("Ошибка удаления документа:", err);
      alert("Не удалось удалить документ");
    }
  };

  // 🔥 Смена статуса
  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/externaltokens/documents/${doc.document_id}/status`, {
        status: newStatus,
      });

      setStatus(newStatus);
      if (onStatusChange) onStatusChange(newStatus);
    } catch (err) {
      console.error("Ошибка обновления статуса:", err);
      alert("Не удалось изменить статус");
    }
  };

  const copyMarks = async (marks) => {
    if (!marks || marks.length === 0) return false;

    const text = marks.map(normalizeMark).join("\n");

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      }

      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();
      const success = document.execCommand("copy");
      document.body.removeChild(textarea);
      return success;
    } catch (e) {
      console.error("Copy failed", e);
      return false;
    }
  };

  const showCopied = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1200);
  };

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: "0.75rem",
        boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
        padding: "1.2rem 1.4rem",
        display: "flex",
        flexDirection: "column",
        gap: "1.1rem",
        border: "1px solid #f2f2f2",
        position: "relative",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.45rem",
          justifyContent: "space-between",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "0.45rem" }}>
          <FaFileAlt size={17} color="var(--brand)" />
          <b style={{ fontSize: "1.05rem" }}>Документ №{doc.document_number}</b>
        </div>

        {/* 🔥 Кнопка удаления */}
        <FaTrash
          size={16}
          color="#e63946"
          style={{ cursor: "pointer" }}
          onClick={() => setShowConfirm(true)}
        />
      </div>

      {/* Статус */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.7rem",
          marginTop: "-0.4rem",
        }}
      >
        <span
          style={{
            padding: "0.25rem 0.55rem",
            borderRadius: "0.35rem",
            background: statusColors[status] + "22",
            color: statusColors[status],
            fontWeight: 600,
            fontSize: "0.85rem",
          }}
        >
          {status}
        </span>

        <select
          value={status}
          onChange={(e) => updateStatus(e.target.value)}
          style={{
            padding: "0.25rem 0.4rem",
            borderRadius: "0.35rem",
            border: "1px solid #ccc",
            fontSize: "0.85rem",
          }}
        >
          {allowedStatuses.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
      </div>

      {/* Grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "auto auto",
          gap: "0.6rem 2rem",
          fontSize: "0.88rem",
        }}
      >
        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>
            Дата документа
          </div>
          <div>{formatDate(doc.document_date)}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>Создан</div>
          <div>{formatDateTime(doc.created_at)}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>Получен</div>
          <div>{formatDateTime(doc.server_received_at)}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>
            Сканирование
          </div>
          <div>{formatDateTime(doc.scanned_at)}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>От кого</div>
          <div>{doc.sent_by_user || "—"}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>
            Комментарий
          </div>
          <div>{doc.comment || "—"}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>
            Маршрут
          </div>
          <div>{doc.route_number || "—"}</div>
        </div>

        <div>
          <div style={{ fontWeight: 600, marginBottom: "0.2rem" }}>Тип</div>
          <div>{doc.document_type || "—"}</div>
        </div>
      </div>

      {/* Items */}
      <div>
        <h4 style={{ fontSize: "0.95rem", margin: "0 0 0.4rem", fontWeight: 600 }}>
          Товары
        </h4>

        {doc.items && doc.items.length > 0 ? (
          <ul
            style={{
              listStyle: "none",
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.6rem",
            }}
          >
            {doc.items.map((item) => (
              <li
                key={item.id}
                onClick={async () => {
                  if (!item.marks?.length) return;
                  const ok = await copyMarks(item.marks);
                  if (ok) showCopied();
                }}
                style={{
                  padding: "0.55rem 0.7rem",
                  background: "#fafafa",
                  borderRadius: "0.45rem",
                  border: "1px solid #ececec",
                  fontSize: "0.9rem",
                  cursor: item.marks?.length ? "pointer" : "default",
                }}
              >
                <b>{item.name}</b> — {item.gtin}

                {item.marks?.length > 0 && (
                  <div style={{ marginTop: "0.4rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.35rem",
                        fontWeight: 600,
                      }}
                    >
                      <FaBarcode size={13} color="var(--brand)" /> Марки
                    </div>

                    <ul
                      style={{
                        paddingLeft: "1.1rem",
                        marginTop: "0.25rem",
                        display: "flex",
                        flexDirection: "column",
                        gap: "0.2rem",
                      }}
                    >
                      {item.marks.map((mark, idx) => (
                        <li key={idx} style={{ fontSize: "0.83rem" }}>
                          {normalizeMark(mark)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p style={{ fontSize: "0.86rem", color: "var(--muted)" }}>
            Товаров нет
          </p>
        )}
      </div>

      {/* ------------------------------- */}
      {/* 🔥 МОДАЛКА ПОДТВЕРЖДЕНИЯ УДАЛЕНИЯ */}
      {/* ------------------------------- */}
      {showConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
          }}
        >
          <div
            style={{
              background: "#fff",
              padding: "20px 25px",
              borderRadius: "10px",
              width: "300px",
              textAlign: "center",
            }}
          >
            <p style={{ fontSize: "16px", marginBottom: "15px" }}>
              Удалить документ №{doc.document_number}?
            </p>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  flex: 1,
                  padding: "8px",
                  borderRadius: "6px",
                  border: "1px solid #ccc",
                }}
              >
                Отмена
              </button>

              <button
                onClick={deleteDocument}
                style={{
                  flex: 1,
                  padding: "8px",
                  background: "#e63946",
                  color: "white",
                  borderRadius: "6px",
                  border: "none",
                }}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {copied && (
        <div
          style={{
            position: "fixed",
            bottom: "1.2rem",
            left: "50%",
            transform: "translateX(-50%)",
            background: "#333",
            color: "white",
            padding: "0.45rem 0.8rem",
            borderRadius: "0.4rem",
            fontSize: "0.85rem",
          }}
        >
          Марки скопированы
        </div>
      )}
    </div>
  );
};

export default DocumentCard;
